import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, CalendarCheck, GraduationCap, Sun, ArrowRight } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { BRAND, Peacock, SpectrumBar, Hashtag } from "@/lib/brand";
import { stashRefFromLocation } from "@/lib/referral";
import { LanguagePicker } from "@/components/LanguagePicker";

// Titles and text live in the locale files under landing.feature.<key>.
const FEATURES = [
  { icon: Mic, key: "talk" },
  { icon: CalendarCheck, key: "agenda" },
  { icon: Sun, key: "nudge" },
  { icon: GraduationCap, key: "learn" },
];

export default function Landing() {
  const { t } = useTranslation();
  useEffect(() => { stashRefFromLocation(); }, []);
  return (
    <div className="relative z-10 min-h-screen">
      <header className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-5">
          <Peacock size={120} className="!w-14 !h-14 sm:!w-20 sm:!h-20 lg:!w-28 lg:!h-28" />
          <span className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="brand" dir="ltr">Vale<span className="text-[var(--focus)]">.</span></span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          <LanguagePicker compact />
          <Link to="/pricing" data-testid="nav-pricing-landing"
            className="text-sm px-4 sm:px-5 py-2.5 rounded-full hover:bg-white transition-colors duration-200">
            {t("landing.pricing")}
          </Link>
          <Link to="/login" data-testid="nav-login"
            className="text-sm px-4 sm:px-5 py-2.5 rounded-full border border-black/10 hover:bg-white transition-colors duration-200">
            {t("auth.signIn")}
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pt-10 md:pt-20 items-end">
          <motion.div className="md:col-span-8" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <SpectrumBar className="max-w-[220px]" />
            <p className="text-sm uppercase tracking-[0.2em] text-[var(--ink-soft)] mt-6">
              <bdi dir="ltr">{BRAND.motto} · {BRAND.mottoTwo}</bdi>
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 tracking-tight">
              {t("landing.title")}{" "}
              <span className="text-[var(--focus)]">{t("landing.titleAccent")}</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed max-w-xl">
              <Trans i18nKey="landing.intro" components={{ tag: <Hashtag /> }} />
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/login" data-testid="hero-start"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--ink)] text-[var(--paper)] hover:bg-[#2e2e2e] transition-colors duration-200">
                {t("landing.cta")}
                <ArrowRight size={17} className="rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
            <p className="mt-5 text-sm text-[var(--ink-soft)] max-w-xl" data-testid="landing-install-hint">
              <Trans i18nKey="landing.installHint"
                components={{ link: <Link to="/install" className="underline underline-offset-4 text-[var(--ink)]" /> }} />
            </p>
          </motion.div>

          <motion.div className="md:col-span-4" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <div className="card p-8 flex flex-col items-center gap-6">
              <div className="breathe h-32 w-32 rounded-full flex items-center justify-center"
                style={{ background: "radial-gradient(circle at 35% 30%, #a9c4bd, #84a59d 65%)" }}>
                <Mic size={34} className="text-white" />
              </div>
              <p className="text-center text-sm text-[var(--ink-soft)] leading-relaxed">
                {t("landing.example.said")}
              </p>
              <div className="w-full border-t border-black/5 pt-5 space-y-2 text-sm">
                <p className="flex justify-between gap-4"><span>{t("landing.example.task1")}</span><span className="text-[var(--focus)]">14:00</span></p>
                <p className="flex justify-between gap-4"><span>{t("landing.example.task2")}</span><span className="text-[var(--focus)]">16:00</span></p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-28">
          {FEATURES.map((f, i) => (
            <motion.div key={f.key} className="card p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}>
              <f.icon size={24} className="text-[var(--calm)]" />
              <h2 className="font-display text-xl sm:text-2xl mt-5 tracking-tight">{t(`landing.feature.${f.key}.title`)}</h2>
              <p className="mt-3 text-sm md:text-base text-[var(--ink-soft)] leading-relaxed">{t(`landing.feature.${f.key}.body`)}</p>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
